import React from 'react'
import styles from './giftCardWallet.module.scss'
import GiftCardWalletCell from './GiftCardWalletCell'
import {
  AnimatedCheckMark,
  Div,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  useDigitalCardsReceived,
  useDigitalGiftCardProducts,
} from 'src/react_query'
import InfiniteScroller from 'src/chrome/InfiniteScroller/InfiniteScroller'
import { useCallback, useState } from 'src/hooks'
import GiftCardWalletLoadingCell from './GiftCardWalletLoadingCell'
import moment from 'moment'
import GiftCardFilter from './GiftCardFilter'
import { DateFilter } from './GiftCardFilterDateField'
import { DigitalGiftCardAmountFilter } from 'src/graphql/generated/graphql'

export type SearchData = {
  giftCardCode?: string
  senderName?: string
  giftCardAmount?: DigitalGiftCardAmountFilter
  paidDate?: DateFilter
}

const GiftCardWallet: React.FC<{}> = () => {
  const redeem = (token: string) => {
    window.location.href = `/act?t=${token}&redeem`
  }

  const [searchData, setSearchData] = useState<SearchData>({})

  const { hasNextPage, fetchNextPage, data, isFetching } =
    useDigitalCardsReceived({
      first: 20,
      filters: {
        hasGiftCard: true,
        ...searchData,
      },
    })

  const handleLoadMore = useCallback(() => {
    fetchNextPage()
    // eslint-disable-next-line
  }, [])

  const gifts = data?.pages
    .flatMap(page =>
      page!.edges.map(edge => {
        return edge.node.recipients[0].giftCards.map(gc => ({
          ...gc,
          expiresAt: edge.node.expiresAt
            ? moment(edge.node.expiresAt).format('MM/DD/YY')
            : '',
          actFrontImgUrl: edge.node.card.frontPreviewUrl,
          sentDate: moment(new Date(edge.node.card.updatedAt)).format(
            'MM/DD/YY',
          ),
          sender: `${edge.node.recipients[0].sender.firstName} ${edge.node.recipients[0].sender.lastName}`,
          token: edge.node.recipients[0].token,
          card: edge.node.card,
        }))
      }),
    )
    .flat()

  const productQueries = useDigitalGiftCardProducts(
    gifts ? gifts.map(g => g.productCodes[0]) : [],
  )

  const handleSearch = (searchData: SearchData) => {
    setSearchData(searchData)
  }

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.titleDesc}>
          <div className={styles.headerTitle}>
            <Text
              type="largeBody"
              content="My Gift Cards"
              weight="bold"
              style={{ fontSize: '1.5rem' }}
            />
          </div>
          <div className={styles.headerDescription}>
            <Text type="caption" content="Here are your saved gift cards" />
          </div>
        </div>
        <div>
          <GiftCardFilter onSearch={handleSearch} />
        </div>
      </div>
      <InfiniteScroller
        hasMore={!!hasNextPage}
        isLoading={isFetching}
        loader={
          <Div inset="x2" overflow="hidden">
            <LoadingSpinner size="xLarge" />
          </Div>
        }
        endMessage={
          <div className={styles.infiniteScrollerEndMessage}>
            <AnimatedCheckMark size="small" />
            <Text type="body">All gift cards loaded.</Text>
          </div>
        }
        loadMore={handleLoadMore}
      >
        <div className={styles.giftsWrapper}>
          {productQueries.length === 0 && isFetching && (
            <GiftCardWalletLoadingCell />
          )}
          {productQueries.map((pq, i) => {
            if (pq.data && gifts && gifts[i]) {
              const gift = gifts[i]
              const giftCellData = {
                brand: pq.data.title,
                brandImgUrl: pq.data.image ?? '',
                actFrontImgUrl: gift.actFrontImgUrl,
                from: gift.sender,
                sentDate: gift.sentDate,
                expirationDate: gift.expiresAt,
                currency: gift.currency,
                amount: `$${gift.amount.split('.')[0]}`,
                onRedeem: () => redeem(gift.token),
                card: gift.card,
              }
              return <GiftCardWalletCell {...giftCellData} />
            } else if (pq.isFetching) {
              return <GiftCardWalletLoadingCell />
            } else {
              return <></>
            }
          })}
        </div>
      </InfiniteScroller>
    </div>
  )
}

export default GiftCardWallet
